import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { CheckedListControl } from "@/Common/Components/Inputs/CheckedListControl";
import { MultiDateSelector } from "@/Common/Components/Inputs/MultiDateSelector";
import { SearchSettingsFooter } from "@/Common/Components/Settings/SearchSettingsFooter";
import { EmployeeHelper } from "@/Common/Helpers/EmployeeHelper";
import { SearchMenuHelper } from "@/Common/Helpers/SearchMenuHelper";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { CheckboxUIModel } from "@/Common/Models/UI/Inputs/CheckboxUIModel";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { TaskSearchSettings } from "@/Communications/Models/Settings/TaskSearchSettings";
import { UserSettings } from "@/Globals/Models/UserSettings";
import { BaseCommunicationSettingsState } from "./BaseCommunicationSettingsState";

import "./TaskSettingsContainer_OptimizedV2.css";

interface ITaskSettingsContainer_OptimizedV2Props {
    onSaveSettingsCallback: (model: TaskSettingsContainer_OptimizedV2State) => void;
    onCloseCallback: () => void;
    onSettingsButtonClick: () => void;
    enableSaveSetting: boolean;

    savedState?: () => TaskSettingsContainer_OptimizedV2State;
}

class TaskSettingsContainer_OptimizedV2State extends BaseCommunicationSettingsState {
    public isAllSharedWithChecked: boolean;
    public isAllStatusesChecked: boolean;
    public sharedWithSearchText: string;
    public sharedWith: EmployeeUIModel[];
    public sharedWithInitial: EmployeeUIModel[];
    public statuses: CheckboxUIModel[];

    public static getDefault(includeUserPreferences: boolean = true): TaskSettingsContainer_OptimizedV2State {
        const s = new TaskSettingsContainer_OptimizedV2State();

        s.setBaseDefault();

        // MYLE-24988, temporary exception for COMTL
        if (cacheStore.ClientIdentifier.toLowerCase() == "comtl")
            s.pageSizeOptions.push(1000);

        s.statuses = cacheStore.getTaskStatuses();
        s.sharedWithSearchText = "";
        s.sharedWith = s.employeesGlobalCache
            .filter(e => cacheStore.EmployeeSharingTasks.contains(e.Id))
            .map(e => e.clone());

        s.isAllSharedWithChecked = false;
        s.sharedWith.mutate(x => x.Checked = false);
        s.sharedWithInitial = s.sharedWith;

        if (includeUserPreferences)
            TaskSettingsContainer_OptimizedV2State.setSearchSettingsFromUserPreferences(s);

        return s;
    }

    private static setSearchSettingsFromUserPreferences(state: TaskSettingsContainer_OptimizedV2State): void {
        const setting = cacheStore.UserSettings.TaskSearchSettings;
        if (setting == null)
            return;

        state.setBaseSearchSettingsFromUserPreferences(setting);

        state.isAllSharedWithChecked = setting.IsAllSharedWithChecked || state.isAllSharedWithChecked;
        state.isAllStatusesChecked = setting.IsAllStatusChecked || state.isAllStatusesChecked;

        if (setting.Status)
            state.statuses.mutate(c => c.Checked = setting.Status.contains(c.Id));

        if (setting.SharedWithIds)
            state.sharedWith.mutate(s => s.Checked = setting.SharedWithIds.contains(s.Id));

        return;
    }
}

class TaskSettingsContainer_OptimizedV2 extends BaseReactComponent<ITaskSettingsContainer_OptimizedV2Props, TaskSettingsContainer_OptimizedV2State> {
    private readonly divContentCssForLazy: React.CSSProperties = { maxHeight: "400px", minHeight: "400px", overflow: "hidden" };
    private readonly divContentCss: React.CSSProperties = { maxHeight: "400px", minHeight: "400px", overflow: "auto" };
    public constructor(props: ITaskSettingsContainer_OptimizedV2Props) {
        super(props);
        this.state = TaskSettingsContainer_OptimizedV2State.getDefault();
    }

    public componentDidMount(): void {
        const savedState = this.props.savedState?.();
        if (savedState) {
            this.setState({
                ...savedState,
                isSaveSettingChecked: false
            });
        }
        super.setOverlay("TaskSettingsContainer_OptimizedV2", { transparent: true, onClicked: () => this.onSaveSettingsCallback() });
    }

    public onSaveSettingsCallback(): void {
        communicationsStore.communicationsTabSavedState.taskSettingContainerSavedState = this.state;

        if (this.state.isSaveSettingChecked)
            this.saveUserSettings();

        this.props.onSaveSettingsCallback(this.state);
    }

    public render(): JSX.Element {
        return (
            <div className="filter-component filter-component-responsive" onClick={e => e && e.stopPropagation()}>
                <table className="f-content">
                    <thead>
                        <tr>
                            <th className="first">
                                <label className="uppercase">{cacheStore.GetString("DueDateLabel")}</label>
                            </th>
                            {this.state.sites.length > 1 && (
                                <th>
                                    <CheckboxControl
                                        id="sites_check"
                                        label={cacheStore.GetString("SitesLabel").toUpperCase()}
                                        checked={this.state.isAllSitesChecked}
                                        onChange={() => this.mutateState(
                                            s => {
                                                s.isAllSitesChecked = !s.isAllSitesChecked;
                                                s.sites.mutate(site => site.Checked = s.isAllSitesChecked);
                                            },
                                            () => this.onSitesChanged()
                                        )}
                                    />
                                </th>
                            )}
                            <th>
                                <CheckboxControl
                                    id="clinicians_check"
                                    label={cacheStore.GetString("CliniciansLabel").toUpperCase()}
                                    checked={this.state.isAllCliniciansChecked}
                                    onChange={() => {
                                        this.mutateState(s => {
                                            s.isAllCliniciansChecked = !s.isAllCliniciansChecked;
                                            s.clinicians.mutate(c => c.Checked = s.isAllCliniciansChecked);
                                        });
                                    }}
                                />
                            </th>
                            <th>
                                <CheckboxControl
                                    id="shared_with"
                                    label={cacheStore.GetString("SharedWithLabel").toUpperCase()}
                                    checked={this.state.isAllSharedWithChecked}
                                    onChange={() => {
                                        this.mutateState(s => {
                                            s.isAllSharedWithChecked = !s.isAllSharedWithChecked;
                                            s.sharedWith.mutate(sw => sw.Checked = s.isAllSharedWithChecked);
                                        });
                                    }}
                                />
                            </th>
                            <th style={{ width: "100px" }}>
                                <CheckboxControl
                                    id="status_check"
                                    label={cacheStore.GetString("StatusDLabel").toUpperCase()}
                                    checked={this.state.isAllStatusesChecked}
                                    onChange={() => {
                                        this.mutateState(s => {
                                            s.isAllStatusesChecked = !s.isAllStatusesChecked;
                                            s.statuses.mutate(sw => sw.Checked = s.isAllStatusesChecked);
                                        });
                                    }}
                                />
                            </th>
                            <th>
                                <CheckboxControl
                                    id="urgency_check"
                                    label={cacheStore.GetString("PriorityLabel").toUpperCase()}
                                    checked={this.state.isAllPrioritiesChecked}
                                    onChange={() => {
                                        this.mutateState(s => {
                                            s.isAllPrioritiesChecked = !s.isAllPrioritiesChecked;
                                            s.priorities.mutate(sw => sw.Checked = s.isAllPrioritiesChecked);
                                        });
                                    }}
                                />
                            </th>
                            <th className="last">
                                <label className="uppercase">{cacheStore.GetString("SortByLabel")}</label>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td className="first">
                                <div>
                                    <MultiDateSelector
                                        id="message_dates"

                                        from={this.state.dateFrom}
                                        fromChange={date => this.mutateState(s => s.dateFrom = date && moment(date).startOf("day").toDate())}
                                        fromSetTodayLabel={cacheStore.GetString("TodayLabel")}

                                        to={this.state.dateTo}
                                        toChange={date => this.mutateState(s => s.dateTo = date && moment(date).endOf("day").toDate())}
                                        toSetTodayLabel={cacheStore.GetString("TodayLabel")}
                                    />
                                </div>
                            </td>
                            {this.state.sites.length > 1 && (
                                <td className="sites-column">
                                    <div style={this.divContentCss}>
                                        <CheckedListControl
                                            DataSource={this.state.sites.toImmutable()}
                                            onChange={(isChecked, id) => this.mutateState(
                                                s => s.sites.mutate(site => site.Checked = isChecked, site => site.Id === id),
                                                () => this.onSitesChanged()
                                            )}
                                            MaxTextLength={25}
                                        />
                                    </div>
                                </td>
                            )}
                            <td style={{ width: "227px" }}>
                                <input
                                    className="form-control"
                                    type="text"
                                    value={this.state.cliniciansSearchText}
                                    onChange={(e: any) => this.onCliniciansFilterTextChanged(e.target.value)}
                                    placeholder={cacheStore.GetString("SearchLabel").toUpperCase()}
                                />
                                <div style={cacheStore.ClinicSettings.EnableLazyRenderingFilterSettingsClinicians ? this.divContentCssForLazy : this.divContentCss}>
                                    <CheckedListControl
                                        DataSource={this.getFilteredClinicians().toImmutable()}
                                        idModifier="cliniciansTask"
                                        onChange={(isChecked, id) => this.mutateState(s => s.clinicians.mutate(c => c.Checked = isChecked, c => c.Id === id))}
                                        sort
                                        limitTextWidth
                                        enableLazyRendering
                                    />
                                </div>
                            </td>
                            <td>
                                <input
                                    className="form-control"
                                    type="text"
                                    value={this.state.sharedWithSearchText}
                                    onChange={(e: any) => this.onCliniciansSharedFilterTextChanged(e.target.value)}
                                    placeholder={cacheStore.GetString("SearchLabel").toUpperCase()}
                                />
                                <div style={this.divContentCss}>
                                    <CheckedListControl
                                        DataSource={this.getFilteredSharedWith().toImmutable()}
                                        idModifier="sharedWithTask"
                                        onChange={(isChecked, id) =>
                                            this.mutateState(s => s.sharedWith.mutate(sw => sw.Checked = isChecked, sw => sw.Id === id))
                                        }
                                    />
                                </div>
                            </td>
                            <td>
                                <CheckedListControl
                                    DataSource={this.state.statuses.toImmutable()}
                                    onChange={(isChecked, id) =>
                                        this.mutateState(s => s.statuses.mutate(sw => sw.Checked = isChecked, sw => sw.Id === id))
                                    }
                                    dataCy="taskSettings-status"
                                />
                            </td>
                            <td>
                                <CheckedListControl
                                    DataSource={this.state.priorities.toImmutable()}
                                    idModifier="PriorityMessage"
                                    onChange={(isChecked, id) =>
                                        this.mutateState(s => s.priorities.mutate(sw => sw.Checked = isChecked, sw => sw.Id === id))
                                    }
                                />
                            </td>
                            <td className="last">
                                <div>
                                    <div onClick={() => this.mutateState(s => s.orderByPriorityFirst = true)}>
                                        <label>
                                            <input type="radio" checked={this.state.orderByPriorityFirst} />
                                            <label style={{ paddingLeft: "5px" }}>{cacheStore.GetString("PriorityHighToLowLabel")}</label>
                                        </label>
                                    </div>
                                    <div onClick={() => this.mutateState(s => s.orderByPriorityFirst = false)}>
                                        <label>
                                            <input type="radio" checked={!this.state.orderByPriorityFirst} />
                                            <label className="captilize-first-letter" style={{ paddingLeft: "5px" }}>{cacheStore.GetString("MessageDateLabel").toLocaleLowerCase()}</label>
                                        </label>
                                    </div>

                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <SearchSettingsFooter
                    footerClassName="task-footer"
                    onClose={() => this.props.onCloseCallback()}
                    onOk={() => this.onSaveSettingsCallback()}
                    onColumnOrderingButtonClick={() => this.props.onSettingsButtonClick()}
                    onResultsPerPageChange={size => this.mutateState(s => s.selectedPageSizeOption = size)}
                    selectedPageSizeOption={this.state.selectedPageSizeOption}
                    pageSizeOptions={this.state.pageSizeOptions}
                    isSaveSettingsChecked={this.state.isSaveSettingChecked}
                    onSaveSettingsCheckboxChange={this.props.enableSaveSetting ? value => this.mutateState(s => s.isSaveSettingChecked = value) : null}
                />
            </div>
        );
    }

    private onCliniciansFilterTextChanged(text: string): void {
        this.mutateState(s => s.cliniciansSearchText = text);
    }

    private onCliniciansSharedFilterTextChanged(text: string): void {
        this.mutateState(s => s.sharedWithSearchText = text);
    }

    private onSitesChanged(): void {
        this.mutateState(s =>
            s.clinicians = EmployeeHelper
                .filterCliniciansByCheckedSites(s.employeesGlobalCache, s.sites)
        );
    }

    private getFilteredClinicians(): EmployeeUIModel[] {
        return EmployeeHelper.filterCliniciansBySearchText(this.state.clinicians, this.state.cliniciansSearchText);
    }

    private getFilteredSharedWith(): EmployeeUIModel[] {
        return SearchMenuHelper.FilterCliniciansByName(
            this.state.sharedWithSearchText,
            this.state.sharedWithInitial.toImmutable()
        ).toArray();
    }

    private saveUserSettings(): void {
        const userSettings = new UserSettings();
        userSettings.TaskSearchSettings = TaskSearchSettings.FromModel(this.state);

        Client.Post(
            ServerOperations.Server.Myle_UpdateUserSettings,
            {
                UserSettings: userSettings,
                UserId: sessionStore.userId
            },
            () => { }
        );
        cacheStore.UserSettings.TaskSearchSettings = userSettings.TaskSearchSettings;
    }
}

export { TaskSettingsContainer_OptimizedV2, TaskSettingsContainer_OptimizedV2State };
